import { useParams } from 'next/navigation';
import { FC, useState } from 'react';
import { Grid, IconButton, Button, InputAdornment, Typography } from '@mui/material';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { CenteredContentWrapper } from '@/components/layout/CenteredContentWrapper/CenteredContentWrapper';
import { useAuthStore } from '@/store';
import { useForm } from 'react-hook-form';
import { PASSWORD_RESET_PHRASES } from '@/consts/phrases/loginPhrases';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { useRouter } from '@/hooks/general/useRouter/useRouter';
import { useHasHydrated } from '@/hooks/general/useHasHydrated';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { PasswordRequirements } from '@/components/forms/other/PasswordRequirements/PasswordRequirements';
import { ICountryData } from '@/types/types/forms/dictionary/dictionary.interface';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';
import { Icon } from '@/components/general/Icon/Icon';

import styles from '@/styles/modules/Auth.module.css';

interface PasswordResetFormValues {
    newPassword: string;
    confirmNewPassword: string;
}

interface PasswordResetFormProps {
    hardReset?: boolean;
    countriesDict?: ICountryData[];
}

const initialData: PasswordResetFormValues = {
    newPassword: '',
    confirmNewPassword: '',
};

export const PasswordResetForm: FC<PasswordResetFormProps> = ({ hardReset, countriesDict }) => {
    const hasHydrated = useHasHydrated();
    const router = useRouter();

    const { t } = usePhrase();

    const { token } = useParams<{ token: string }>();

    const passwordReset = useAuthStore((state) => state.passwordReset);

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const { control, handleSubmit, formState, getValues, setError, clearErrors } =
        useForm<PasswordResetFormValues>({
            defaultValues: initialData,
        });

    const { handleServerErrors, serverErrors, clearAllErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const { errors, isSubmitting } = formState;

    const handleClickShowPassword = () => {
        setShowPassword((prevState) => !prevState);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword((prevState) => !prevState);
    };

    const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handlePasswordReset = async ({
        newPassword,
        confirmNewPassword,
    }: PasswordResetFormValues) => {
        const res = await passwordReset(
            newPassword,
            confirmNewPassword,
            token,
            hardReset,
            countriesDict,
        );
        if (res.type === 'success') {
            clearAllErrors();
            if (hardReset) {
                router.push('/surveys');
            } else {
                router.push('/forgotten-password/reset-successful');
            }
        } else {
            handleServerErrors(res.error);
        }
    };

    const onSubmit = async (vals: PasswordResetFormValues) => {
        await handlePasswordReset(vals);
    };

    return (
        <CenteredContentWrapper>
            <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                aria-label="reset_password_form"
                className={styles.auth}
            >
                <Grid container spacing={1.5}>
                    {hardReset ? (
                        <Grid size={12}>
                            <Typography variant="h2" align="center" className={styles.title}>
                                {t(PASSWORD_RESET_PHRASES.CREATE_PASSWORD)}
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                align="center"
                                className={`${styles.titleDescription} LoginTitleDescription`}
                            >
                                {t(PASSWORD_RESET_PHRASES.CREATE_PASSWORD_DESCRIPTION)}
                            </Typography>
                        </Grid>
                    ) : (
                        <Grid size={12}>
                            <Typography variant="h2" align="center" className={styles.title}>
                                {t(PASSWORD_RESET_PHRASES.NEW_PASSWORD)}
                            </Typography>
                        </Grid>
                    )}

                    {hasHydrated && (
                        <FormGridErrors
                            errors={serverErrors}
                            gridClassName={styles.errorBlock}
                            generalErrorId="message_box"
                        />
                    )}

                    <Grid size={12}>
                        <TextField
                            control={control}
                            name="newPassword"
                            id="password_field"
                            type={showPassword ? 'text' : 'password'}
                            required
                            fullWidth
                            label={PASSWORD_RESET_PHRASES.NEW_PASSWORD}
                            size="small"
                            error={errors.newPassword}
                            data-testid="password-input"
                            autoComplete="new-password"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                            >
                                                {showPassword ? (
                                                    <Icon path="general/controlIcons/VisibilityOffIcon" />
                                                ) : (
                                                    <Icon path="general/controlIcons/VisibilityIcon" />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={12} sx={{ mt: 1 }}>
                        <TextField
                            control={control}
                            name="confirmNewPassword"
                            id="confirm_password_field"
                            type={showConfirmPassword ? 'text' : 'password'}
                            required
                            fullWidth
                            label={PASSWORD_RESET_PHRASES.REPEAT_NEW_PASSWORD}
                            size="small"
                            error={errors.confirmNewPassword}
                            data-testid="confirm-password-input"
                            autoComplete="new-password"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle confirm password visibility"
                                                onClick={handleClickShowConfirmPassword}
                                                onMouseDown={handleMouseDownConfirmPassword}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? (
                                                    <Icon path="general/controlIcons/VisibilityOffIcon" />
                                                ) : (
                                                    <Icon path="general/controlIcons/VisibilityIcon" />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Grid>
                    <Grid size={12}>
                        <PasswordRequirements />
                    </Grid>
                    <Grid size={12} sx={{ mt: 1 }}>
                        <Button
                            fullWidth
                            id="set-new-password"
                            type="submit"
                            variant="contained"
                            loading={isSubmitting}
                        >
                            {t(PASSWORD_RESET_PHRASES.SET_NEW_PASSWORD)}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </CenteredContentWrapper>
    );
};
